/**
 * SubscriptionStatusAlert — banner + modal when subscription is expired.
 *
 * Two pieces, one file:
 *   1. <SubscriptionExpiredBanner /> — persistent at top of every authed
 *      page. Visible until subscription is renewed. Read-only for cashiers
 *      ("Your tenant_owner needs to renew"). Actionable for tenant_owner
 *      ("Renew now" button).
 *   2. <SubscriptionExpiredModal /> — shown ONCE per session on cashier
 *      login. Friendly framing: explains the platform isn't broken,
 *      the subscription needs renewing. Dismissable. Won't reappear until
 *      next login OR until a tenant_owner renews.
 *
 * Reads the subscription state from the auth user object. Expects:
 *   user.subscriptionStatus: 'active' | 'trial' | 'expired' | 'cancelled'
 *   user.planTier: 'Basic' | 'Standard' | 'Premium' | 'Enterprise'
 *   user.subscriptionEndDate: ISO string
 *   user.role: UserRole
 *
 * If your auth store hydrates these from /auth/me or /subscriptions/status,
 * this component picks them up automatically. If you store them elsewhere,
 * point the `useSubscriptionStatus` hook below at the right source.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CreditCard,
  Lock,
  X,
  CheckCircle2,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import useAuthStore from "@/stores/authStore";

// ─── Subscription status hook (read from auth store) ───────────────

type SubscriptionStatus =
  | "active"
  | "trial"
  | "expired"
  | "cancelled"
  | "unknown";

interface SubscriptionState {
  status: SubscriptionStatus;
  isExpired: boolean;
  planTier?: string;
  endDate?: string | null;
  daysSinceExpiry: number;
}

function useSubscriptionStatus(): SubscriptionState {
  const user = useAuthStore((s) => s.user);
  const u = user as
    | (typeof user & {
        subscriptionStatus?: string;
        planTier?: string;
        subscriptionEndDate?: string | null;
      })
    | null;

  const rawStatus = (u?.subscriptionStatus || "unknown") as SubscriptionStatus;
  const planTier = u?.planTier;
  const endDate = u?.subscriptionEndDate || null;

  const isExpired = rawStatus === "expired" || rawStatus === "cancelled";

  let daysSinceExpiry = 0;
  if (isExpired && endDate) {
    const end = new Date(endDate).getTime();
    const now = Date.now();
    daysSinceExpiry = Math.max(
      0,
      Math.floor((now - end) / (1000 * 60 * 60 * 24)),
    );
  }

  return {
    status: rawStatus,
    isExpired,
    planTier,
    endDate,
    daysSinceExpiry,
  };
}

// ─── Banner — persistent at top of authed layout ───────────────────

export const SubscriptionExpiredBanner: React.FC = () => {
  const status = useSubscriptionStatus();
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  if (!status.isExpired) return null;

  const isOwner = user?.role === "tenant_owner";
  const message =
    status.daysSinceExpiry > 0
      ? `Subscription expired ${status.daysSinceExpiry} day${status.daysSinceExpiry === 1 ? "" : "s"} ago`
      : "Subscription expired";

  return (
    <div
      role="alert"
      className="w-full bg-amber-50 border-b border-amber-300 text-amber-900"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-semibold text-sm">{message}.</span>{" "}
          <span className="text-xs text-amber-800">
            {isOwner
              ? "Renew to keep recording sales and editing data. Read-only access continues for now."
              : "Your tenant owner needs to renew. The system isn't broken — your access is read-only until renewal."}
          </span>
        </div>
        {isOwner && (
          <Button
            size="sm"
            onClick={() => navigate("/subscriptions")}
            className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
          >
            <CreditCard className="h-3.5 w-3.5 mr-1.5" />
            Renew now
          </Button>
        )}
      </div>
    </div>
  );
};

// ─── Modal — one-time on login per session ─────────────────────────

const SESSION_KEY = "eaze-subscription-expired-modal-shown";

export const SubscriptionExpiredModal: React.FC = () => {
  const status = useSubscriptionStatus();
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!status.isExpired) return;
    if (!user) return;
    try {
      const alreadyShown = sessionStorage.getItem(SESSION_KEY);
      if (alreadyShown) return;
    } catch {
      // sessionStorage might be blocked — still show
    }
    setOpen(true);
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* ignore */
    }
  }, [status.isExpired, user]);

  // Clear the session flag when subscription becomes active again,
  // so a future expiry shows the modal again.
  useEffect(() => {
    if (!status.isExpired) {
      try {
        sessionStorage.removeItem(SESSION_KEY);
      } catch {
        /* ignore */
      }
    }
  }, [status.isExpired]);

  if (!status.isExpired) return null;

  const isOwner = user?.role === "tenant_owner";
  const isCashier = user?.role === "cashier";

  const handleAction = () => {
    setOpen(false);
    if (isOwner) {
      navigate("/subscriptions");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mx-auto mb-2">
            <Lock className="h-6 w-6 text-amber-700" />
          </div>
          <DialogTitle className="text-center">
            Subscription expired
          </DialogTitle>
          <DialogDescription className="text-center pt-2">
            {isCashier ? (
              <>
                The Eaze system is working normally — your business's
                subscription just needs to be renewed. You can still view
                products, customers, and recent sales, but new sales can't be
                recorded until renewal.
              </>
            ) : isOwner ? (
              <>
                Your Eaze subscription has ended. Renew now to resume recording
                sales, posting journal entries, and using the full platform.
              </>
            ) : (
              <>
                The subscription for this business has ended. Your tenant owner
                will need to renew. Until then, you have read-only access.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md bg-muted/50 p-3 my-2 space-y-2 text-sm">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
            <span>
              <strong>What still works:</strong> viewing all data, dashboards,
              reports, products, customers, past sales.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <span>
              <strong>What's blocked:</strong> recording new sales, creating
              expenditures, adding stock, editing data.
            </span>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="w-full sm:w-auto"
          >
            {isOwner ? "Maybe later" : "I understand"}
          </Button>
          {isOwner && (
            <Button
              onClick={handleAction}
              className="bg-[#4CAF50] hover:bg-[#45A049] text-white w-full sm:w-auto"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Renew subscription
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Combined export — drop one component into AuthedLayout ────────

const SubscriptionStatusAlert: React.FC = () => (
  <>
    <SubscriptionExpiredBanner />
    <SubscriptionExpiredModal />
  </>
);

export default SubscriptionStatusAlert;
